/**
 * Canonical types for the Submissions v2 API (orion-backend PR #253).
 *
 * Two layers live here on purpose:
 *
 *   1. **Wire types** mirror the server payload one-to-one — snake_case
 *      keys exactly as `src/schemas/v2/submission/*` in orion-backend.
 *      No case translation: the FE consumes the JSON as the server emits it.
 *   2. **Form types** (`Submission*Form`, `SubmissionForm`) preserve the
 *      grouped shape that the UI sections consume — `accountBasics`,
 *      `brokerage`, `geminiContacts`, etc. They are not the wire shape.
 *
 * Adapters in `src/api/adapters/submission.ts` translate between the two.
 */

// ──────────────────────────── Enums ────────────────────────────

// Mirrors the backend v2 `SubmissionStatus` enum (orion-backend
// src/schemas/v2/submission/submission.py). These are the exact 8 values
// the API accepts today — any other value 422s. Updated per OR-1265:
// added "Indications Sent", renamed "Quoted" → "Quote Sent" and
// "Declined" → "Risk Denied" + "Risk Closed by Agent". Backend prevails.
// Dot colors per design: Indications Sent = warning, Quote Sent = brand,
// Risk Denied + Risk Closed by Agent = error.
export type SubmissionStatus =
    | "Draft"
    | "Awaiting BOR"
    | "Underwriting"
    | "Indications Sent"
    | "Quote Sent"
    | "Bound"
    | "Risk Denied"
    | "Risk Closed by Agent";

export const SUBMISSION_STATUSES: readonly SubmissionStatus[] = [
    "Draft",
    "Awaiting BOR",
    "Underwriting",
    "Indications Sent",
    "Quote Sent",
    "Bound",
    "Risk Denied",
    "Risk Closed by Agent",
] as const;

export type SubmissionType = "New Business" | "Renewal" | "Endorsement" | "Tail";

export const SUBMISSION_TYPES: readonly SubmissionType[] = ["New Business", "Renewal", "Endorsement", "Tail"] as const;

export type AdditionalInsuredCoverage = "PL" | "GL" | "PL/GL";

// All 50 US states, mirrors src/schemas/v2/submission/state.py
export const US_STATES = [
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
] as const;
export type USState = (typeof US_STATES)[number];

// ──────────────────────── Wire types (server) ──────────────────

// ──────────────── Submission Triage list (?view=submission|facility) ────────
// The list endpoint serves two shapes behind `?view=`: a row-per-submission
// "Deals" view and a row-per-facility view (each submission's locations
// fanned out). Both embed nested objects (contact / schedule / quote /
// facility) plus `assignee`/`approver` as user objects. Decimal money fields
// arrive as strings (DRF Decimal); bed counts / ratios as numbers.

export type TriageViewParam = "submission" | "facility";

/** Mini user object embedded in triage rows (assignee / approver). */
export interface UserDisplay {
    id: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
}

/** `?view=submission` — one row per submission. */
export interface SubmissionViewItemWire {
    id: string;
    title: string | null;
    renewal_date: string | null;
    effective_date?: string | null;
    status: SubmissionStatus | null;
    type: SubmissionType | null;
    submission_date: string | null;
    expiring_premium: number | null;
    expiring_deductible: number | null;
    hubspot_id: string | null;
    note: string | null;
    probability_to_bind: string | null;
    brokerage: { id: string; name: string | null } | null;
    /** Brokerage as typed on the submission; the cell's fallback when no
     *  broker is linked (OR-1789). Absent on backends that predate it. */
    brokerage_name?: string | null;
    contact: {
        risk_manager_name: string | null;
        risk_manager_phone: string | null;
        risk_manager_email: string | null;
    } | null;
    schedule: {
        num_facilities: number | null;
        num_skilled_beds: number | null;
        num_assisted_beds: number | null;
        num_independent_beds: number | null;
        num_additional_beds: number | null;
        pl_retro_date: string | null;
    } | null;
    quote: {
        declared_sir: string | null;
        selected_premium: string | null;
        tria_fee_amount: string | null;
        tech_rm_fee: string | null;
        agent_commission_rate: string | null;
        tricura_commission_rate: string | null;
        deviation_from_unity: string | null;
    } | null;
    assignee: UserDisplay | null;
    approver: UserDisplay | null;
    policy: { policy_number: string | null } | null;
    /** Open-comment counts per commentable cell (OR-1535). Optional so the
     *  table keeps working against a backend that predates the feature. */
    comments?: {
        num_open_comments: number;
        fields: { field: string | null; num_open_comments: number }[];
    };
}

/** `?view=facility` — one row per facility (location). */
export interface FacilityViewItemWire {
    submission_id: string;
    location_id: string;
    facility: { id: string; name: string | null } | null;
    cms_number: string | null;
    renewal_date: string | null;
    type: SubmissionType | null;
    status: SubmissionStatus | null;
    submission_date: string | null;
    brokerage: { id: string; name: string | null } | null;
    /** See `SubmissionViewItemWire.brokerage_name`. */
    brokerage_name?: string | null;
    broker_name: string | null;
    schedule: {
        num_skilled_beds: number | null;
        num_assisted_beds: number | null;
        num_independent_beds: number | null;
        num_additional_beds: number | null;
        address: string | null;
        city: string | null;
        state: string | null;
        /** OPTIONAL: not in the backend's triage schedule yet (asked for under
         *  OR-1387 alongside the CCN / address / zip search). Absent → null. */
        zip_code?: string | null;
        county: string | null;
        urbanicity: string | null;
        num_licensed_beds: number | null;
        num_occupied_beds: number | null;
        occupancy_ratio: number | null;
        pl_retro_date: string | null;
        gl_retro_date: string | null;
    } | null;
    assignee: UserDisplay | null;
    approver: UserDisplay | null;
    policy: { policy_number: string | null } | null;
}

export type TriageItemWire = SubmissionViewItemWire | FacilityViewItemWire;

export interface SubmissionListResponse {
    view: TriageViewParam;
    total: number;
    items: TriageItemWire[];
}

export interface SubmissionContact {
    submission_id: string;
    risk_manager_name: string | null;
    risk_manager_email: string | null;
    risk_manager_phone: string | null;
    administrator_name: string | null;
    administrator_email: string | null;
    administrator_phone: string | null;
    administrator_duration_months: number | null;
    created_at: string;
    updated_at: string;
}

export interface SubmissionNamedInsuredWire {
    id: string;
    submission_id: string;
    entity_name: string | null;
    relationship: string | null;
    retroactive_date: string | null;
    created_at: string;
    updated_at: string;
}

export interface SubmissionAdditionalInsuredWire {
    id: string;
    submission_id: string;
    entity_name: string | null;
    coverage: AdditionalInsuredCoverage | null;
    retroactive_date: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * Submission location row. PR #253 shipped the address subset; PR #261
 * added the full Schedule shape (county/urbanicity, retention, limits,
 * bed counts and the computed premium columns). Mirrors
 * `SubmissionLocationGet` in orion-backend.
 */
export interface SubmissionLocationWire {
    id: string;
    submission_id: string;
    location_name: string | null;
    /** CMS / CCN number returned by the locations endpoint — feeds the
     *  Facility Schedule "Provider Number" column. */
    cms_number?: string | null;
    /** Legacy provider-number fallback (pre-`cms_number` responses). */
    provider_number?: string | null;
    acquired_date?: string | null;
    /** Operator name. */
    operator_name?: string | null;
    /** Set (non-null) to the original submission's id when this facility
     *  is detected as a duplicate of a previously-submitted location. */
    duplicate_submission_id?: string | null;
    address: string | null;
    city: string | null;
    state: USState | null;
    zip_code: string | null;
    county: string | null;
    /** Free string on the backend — "Urban" | "Rural" in practice. */
    urbanicity: string | null;
    pl_retroactive_date: string | null;
    gl_retroactive_date: string | null;
    /** Retention / Self-Insured Retention. */
    sir: number | null;
    pl_limit: number | null;
    gl_limit: number | null;
    /** FK into the global facility catalog (`/v2/facilities`). */
    facility_id: string | null;
    skilled_beds: number | null;
    assisted_beds: number | null;
    independent_beds: number | null;
    additional_beds: number | null;
    skilled_beds_licensed: number | null;
    assisted_beds_licensed: number | null;
    independent_beds_licensed: number | null;
    memory_care_beds_licensed: number | null;
    licensed_beds: number | null;
    // Computed premium columns — the FE calculates these client-side
    // and does NOT persist them back (see endpoints/facilities.ts).
    equivalency: number | null;
    actuarial_rate: number | null;
    unadjusted_premium: number | null;
    maturity_credit: number | null;
    maturity_adjusted_premium: number | null;
    sir_credit: number | null;
    sir_adjusted_premium: number | null;
    /** Archived ("excluded") flag — OR-1419/OR-1431. The row stays on the
     *  submission (the list endpoint keeps returning it) but the backend drops
     *  it from the premium totals, the loss-run summary and the surplus-lines
     *  tax request. Absent on responses that predate the column. */
    is_excluded?: boolean | null;
    created_at: string;
    updated_at: string;
}

/** Submission relativity coefficients — embedded in the submission GET,
 *  and writable via `PUT /v2/submissions/{id}/relativity/`. */
export interface SubmissionRelativityWire {
    submission_id: string;
    skilled_rate: number;
    assisted_rate: number;
    independent_rate: number;
    additional_rate: number;
}

/** Full GET /v2/submissions/{id} response. */
export interface SubmissionWire {
    id: string;
    title: string;
    fni: string | null;
    /** OR-1455 — optional on the wire until orion-backend PR #326 deploys. */
    /** OR-1455 — wire key provisional, no backend column yet. */
    business_description?: string | null;
    mailing_address?: string | null;
    type: SubmissionType | null;
    status: SubmissionStatus | null;
    renewal_date: string | null;
    /** Policy-period start the Ascend invoice bills on — a DIFFERENT
     *  column from renewal_date. Optional: older payloads omit it. */
    effective_date?: string | null;
    expiration_date: string | null;
    submission_date: string | null;
    expiring_premium: number | null;
    expiring_deductible: number | null;
    account_manager_name: string | null;
    account_manager_email: string | null;
    broker_id: string | null;
    broker_email: string | null;
    broker_name: string | null;
    brokerage_address: string | null;
    brokerage_name: string | null;
    note: string | null;
    probability_to_bind: string | null;
    dropbox_url: string | null;
    assignee_id: number | null;
    approver_id: number | null;
    /** BOR received status. `null` when no duplicate has been detected,
     *  `false` when a duplicate facility was detected and the BOR is
     *  still pending, `true` once the BOR is received / conflict
     *  resolved. Drives the Schedule "Duplicate Submission" warning. */
    is_bor_received: boolean | null;
    created_at: string;
    updated_at: string;
    contact: SubmissionContact | null;
    named_insureds: SubmissionNamedInsuredWire[];
    additional_insureds: SubmissionAdditionalInsuredWire[];
    locations: SubmissionLocationWire[];
    relativity: SubmissionRelativityWire | null;
}

// ───────────────── Form-friendly types (UI shape) ──────────────

export interface AccountBasicsForm {
    title: string;
    fni: string;
    /** Free-text description of the insured's business. The Policy Form's
     *  dec replaces its "Senior Living Communities" anchor with it (OR-1455). */
    businessDescription: string;
    /** Named insured's mailing address — the Policy Form's dec page prints
     *  it separately from the primary location's address (OR-1455). */
    mailingAddress: string;
    type: SubmissionType | "";
    renewalDate: string;
    expirationDate: string;
    submissionDate: string;
    expiringPremium: string;
    expiringDeductible: string;
    accountManagerName: string;
    accountManagerEmail: string;
}

export interface BrokerageForm {
    brokerId: string;
    brokerEmail: string;
    brokerName: string;
    brokerageName: string;
    brokerageAddress: string;
}

export interface GeminiContactsForm {
    riskManagerName: string;
    riskManagerEmail: string;
    riskManagerPhone: string;
    administratorName: string;
    administratorEmail: string;
    administratorPhone: string;
    /** Free-text input; persisted as months (see adapter). */
    administratorDuration: string;
    /** UI-only; backend stores months. */
    administratorDurationUnit: "years" | "months";
}

export interface NamedInsuredForm {
    /** Empty when the row is brand-new client-side; populated by the
     *  server after the first POST. The save flow uses `id === ""` as
     *  the signal to POST instead of PATCH. */
    id: string;
    entityName: string;
    /** Free-form string. Picked from a preset list by default
     *  (parent / subsidiary / affiliate / dba) but the table allows
     *  the user to add a custom label via "Custom Value". */
    relationship: string;
    retroactiveDate: string;
}

export interface AdditionalInsuredForm {
    id: string;
    entityName: string;
    /** Form-side boolean. Adapter combines (gl,pl) into the wire enum
     *  `"PL" | "GL" | "PL/GL" | null`. */
    gl: boolean;
    pl: boolean;
    retroactiveDate: string;
}

export interface LocationForm {
    id: string;
    locationName: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    glRetroactiveDate: string;
    plRetroactiveDate: string;
}

// ─────────── Schedule (form-only — not yet on v2 backend) ─────────
//
// The Schedule section's persistence is not part of PR #253. We keep
// the data in-memory on the SubmissionForm so the page still works
// during a session; ship it to the backend in a follow-up.

export interface RelativityRow {
    skilled: string;
    assisted: string;
    independent: string;
    additionalExposure: string;
}

export interface FacilityScheduleRow {
    id: string;
    facilityName: string;
    acquired: string;
    operator: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    geo: string;
    county: string;
    plRetro: string;
    glRetro: string;
    retention: string;
    plLimit: string;
    glLimit: string;
    occupancy: string;
    skilled: string;
    assisted: string;
    independent: string;
    addlExposure: string;
    rate: string;
    unadjPrem: string;
    matCredit: string;
    matAdjPrem: string;
    sirCredit: string;
    sirAdjPrem: string;
    licSnf: string;
    licAlf: string;
    licIlf: string;
}

export interface StateDistributionRow {
    id: string;
    state: string;
    exposure: string;
    rates: string;
}

export interface ScheduleSection {
    rateUnadjusted: string;
    rateAdjustedOccupancy: string;
    relativity: RelativityRow;
    facilities: FacilityScheduleRow[];
    stateDistribution: StateDistributionRow[];
}

/** Form-side mirror of the full submission detail. The grouping
 *  (`accountBasics`, `brokerage`, …) is a UI affordance — the server
 *  payload is flat. See `toWireSubmission()` / `fromWireSubmission()`. */
export interface SubmissionForm {
    id: string;
    status: SubmissionStatus;
    /** Numeric user id (`assignee_id` / `approver_id` on the wire). */
    assignee: number | null;
    approver: number | null;
    /** Mirrors `SubmissionWire.is_bor_received`. `false` → show the
     *  Schedule duplicate-submission warning; `null`/`true` → hide it. */
    isBorReceived: boolean | null;
    /** External documents link (Dropbox). "" when none — drives the
     *  Files page "View External Documents" banner. */
    dropboxUrl: string;
    /** Underwriter judgment — "Low" | "Medium" | "High" | "". Edited on
     *  the Premium Selection page, persisted via PATCH submission. */
    probabilityToBind: string;
    /** ISO datetime of the submission's last update (wire `updated_at`).
     *  Used as the "Updated {date · time}" proxy for the generated docs
     *  (Quote / Binder) on the Files page — there is no real per-document
     *  generated-at timestamp. "" when the wire omits it. */
    updatedAt: string;
    accountBasics: AccountBasicsForm;
    brokerage: BrokerageForm;
    geminiContacts: GeminiContactsForm;
    note: string;
    namedInsureds: NamedInsuredForm[];
    additionalInsureds: AdditionalInsuredForm[];
    locations: LocationForm[];
    /** Form-only — see comment on `ScheduleSection`. */
    schedule: ScheduleSection;
}

// ──────────────────────────── Brokerages ─────────────────────────
//
// Backend exposes brokerages (companies) at `/v2/brokerages/` —
// the parents of the broker-people served at `/v2/brokers/`.

export interface BrokerageRow {
    id: string;
    name: string;
    address: string | null;
    city: string | null;
    state: string | null;
    zip_code: string | null;
    email_domain: string | null;
    phone: string | null;
    hubspot_id: string | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface BrokerageCreateInput {
    name: string;
    address: string;
    city: string;
    state: string;
    zip_code: string;
    email_domain?: string;
    phone?: string;
}

// ──────────────────────────── Brokers ────────────────────────────
//
// Brokers (formerly "agents" on the v1 API) are people that broker on
// behalf of a brokerage company. The Brokerage section's selector
// picks a broker — the broker's id is the value of
// `submission.broker_id`. The backend's GET responses include the
// embedded `brokerage` relation so the form can pull both the broker
// fields (name + email) and the brokerage fields (name + address)
// from a single row.

export interface AgentRow {
    id: string;
    name: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    brokerage_id: string | null;
    /** Embedded brokerage record; null when the broker has none. */
    brokerage: BrokerageRow | null;
}

export interface AgentCreateInput {
    /** brokerage to attach the broker to */
    brokerage_id: string;
    name?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
}
